import { useCallback, useState } from 'react';

/**
 * Ob ein Kapitel des Feed-Checks (Preis, Ads) aufgeklappt ist, gemerkt pro
 * Feed. Muster identisch zu `railWidthPreference.ts` / `statusScope.ts`: der
 * Zustand haengt an der Menge, die gerade vor einem liegt, nicht global — wer
 * bei Feed A das Preis-Kapitel geoeffnet hat, soll bei Feed B nicht automatisch
 * dieselbe Ansicht vorfinden.
 *
 * Default ist IMMER zu. Die zwei Kapitel sind der Grund, warum die Seite
 * vorher 6,3 Bildschirme hoch war (UX-Audit 09/2026); ein Default "offen"
 * haette an dieser Entlastung nichts geaendert.
 *
 * `ads_leaks` ist die Geldliste INNERHALB des Ads-Kapitels, nicht das Kapitel
 * selbst. Sie braucht einen eigenen Schluessel, weil sonst der Aufklapper des
 * Kapitels und der der Liste denselben Speicherplatz teilen wuerden: das
 * Kapitel zu oeffnen klappte dann die Liste mit auf, und das Zuklappen der
 * Liste schloesse das Kapitel.
 */
export type ChapterId = 'price' | 'ads' | 'ads_leaks' | 'live';

/** Praefix des localStorage-Schluessels. Kapitel und Feed bilden den Rest. */
export const CHAPTER_OPEN_STORAGE_PREFIX = 'fo.feedcheck.chapter';

export function chapterOpenStorageKey(feedId: string, chapter: ChapterId): string {
  return `${CHAPTER_OPEN_STORAGE_PREFIX}.${chapter}.${feedId}`;
}

/**
 * Liest, ob ein Kapitel fuer diesen Feed aufgeklappt ist.
 *
 * Ohne `feedId` wird nicht gelesen (Default false) — ein feedloser Schluessel
 * waere ein gemeinsamer Zustand fuer verschiedene Feeds, und genau das soll
 * dieser Speicher verhindern. Ein werfender `localStorage` (Safari-
 * Privatmodus, volle Quote) darf niemals die Seite reissen: Zugriffsfehler
 * fallen auf den sicheren Default zurueck.
 */
export function readChapterOpen(feedId: string | null, chapter: ChapterId): boolean {
  if (!feedId) return false;
  try {
    return window.localStorage.getItem(chapterOpenStorageKey(feedId, chapter)) === '1';
  } catch {
    return false;
  }
}

/** Gegenstueck zu `readChapterOpen`. Schluckt Speicherfehler bewusst. */
export function writeChapterOpen(feedId: string | null, chapter: ChapterId, open: boolean): void {
  if (!feedId) return;
  try {
    if (open) {
      window.localStorage.setItem(chapterOpenStorageKey(feedId, chapter), '1');
    } else {
      window.localStorage.removeItem(chapterOpenStorageKey(feedId, chapter));
    }
  } catch {
    // Nicht speichern zu koennen ist kein Fehler, der den Nutzer erreichen muss.
  }
}

/**
 * Kapitel-offen-Zustand mit Gedaechtnis pro Feed.
 *
 * Der Feedwechsel wird WAEHREND des Renders nachgezogen, nicht per Effect —
 * dasselbe Muster wie `useRailWidthPreference` / `useStatusPreference`. Ein
 * Effect liesse einen Frame zu, in dem `feedId` schon der neue Feed ist, der
 * Aufklapp-Zustand aber noch der des alten.
 */
export function useChapterOpen(
  feedId: string | null,
  chapter: ChapterId,
): [boolean, (open: boolean) => void] {
  const [state, setState] = useState<{ feedId: string | null; open: boolean }>(() => ({
    feedId,
    open: readChapterOpen(feedId, chapter),
  }));

  if (state.feedId !== feedId) {
    setState({ feedId, open: readChapterOpen(feedId, chapter) });
  }

  const setOpen = useCallback(
    (open: boolean) => {
      setState({ feedId, open });
      writeChapterOpen(feedId, chapter, open);
    },
    [feedId, chapter],
  );

  return [state.open, setOpen];
}
